@if (!isLoading) {
  <div class="paragraph-builder-container">
    <!-- Top Bar with Breadcrumb and Actions -->
    <div class="top-bar">
      <div class="breadcrumb-section">
        <button mat-icon-button (click)="goBack()" matTooltip="Back to Form Finder">
          <lucide-icon name="arrow-left" [size]="20"></lucide-icon>
        </button>
        @if (selectedCfgForm) {
          <div class="breadcrumb">
            <span class="breadcrumb-item form-name">
              <lucide-icon name="file-text" [size]="20"></lucide-icon>
              {{ selectedCfgForm.DFormName }}
            </span>
            <lucide-icon name="chevron-right" [size]="20" class="breadcrumb-separator"></lucide-icon>
            @if (selectedCfgFormVersion) {
              <span class="breadcrumb-item version">
                <lucide-icon name="history" [size]="20"></lucide-icon>
                Version {{ selectedCfgFormVersion.Version }}.{{ selectedCfgFormVersion.Revision }}
              </span>
            }
            @if (selectedCfgFormParagraph) {
              <lucide-icon name="chevron-right" [size]="20" class="breadcrumb-separator"></lucide-icon>
            }
            @if (selectedCfgFormParagraph) {
              <span class="breadcrumb-item paragraph">
                <lucide-icon name="align-justify" [size]="20"></lucide-icon>
                {{ selectedCfgFormParagraph.ParagraphName }}
              </span>
            }
          </div>
        }
      </div>
      <div class="top-bar-actions">
        <!-- Preview view controls, hoisted out of the preview component so the editor keeps one
             header row. Only meaningful while the preview is on screen — and deliberately placed
             LEFT of the mode toggle: .top-bar-actions is right-anchored, so anything inserted here
             grows leftward and leaves the toggle and the buttons after it stationary. -->
        @if (centerMode === 'preview') {
          <button mat-stroked-button
            class="preview-layout-toggle"
            (click)="togglePreviewLayoutMode()"
            [matTooltip]="previewUseDocumentFrame ? 'Switch to the in-app document view' : 'Switch to the paper page view'"
            [disabled]="!selectedCfgFormVersion"
            data-cy="paragraph-builder-preview-layout">
            <mat-icon>{{ previewUseDocumentFrame ? 'article' : 'view_stream' }}</mat-icon>
            {{ previewUseDocumentFrame ? 'TDOS Print Preview' : 'TDOS Document View' }}
          </button>
          <mat-checkbox class="preview-mobile-checkbox"
            [(ngModel)]="previewMobile"
            [disabled]="!selectedCfgFormVersion"
            data-cy="paragraph-builder-preview-mobile">
            Mobile preview
          </mat-checkbox>
        }
        <button mat-raised-button
          color="accent"
          (click)="openAiStudio('chat')"
          [disabled]="!formIsEditable || !selectedCfgFormVersion"
          matTooltip="Edit the form using natural language">
          <mat-icon>auto_awesome</mat-icon>
          AI Edit
        </button>
        <!-- Center pane mode: a fixed-geometry segmented control with a sliding thumb. The segments
             carry labels because these two affordances used to be icon-only and were routinely
             missed, and the fixed width keeps this right-anchored toolbar from reflowing.
             Deliberately a group of toggle buttons rather than a tablist — the ARIA tab pattern owes
             assistive tech a roving tabindex, arrow-key navigation and an aria-controls'd tabpanel,
             none of which this control has. aria-pressed states the same thing honestly, and native
             buttons already handle Tab/Enter/Space. -->
        <div class="mode-switch"
          [class.is-disabled]="!selectedCfgFormVersion"
          role="group"
          aria-label="Editor mode"
          data-cy="paragraph-builder-center-mode">
          <span class="mode-switch-thumb"
            [class.at-preview]="centerMode === 'preview'"
            aria-hidden="true"></span>
          <button type="button"
            class="mode-switch-option"
            [class.is-active]="centerMode === 'edit'"
            [attr.aria-pressed]="centerMode === 'edit'"
            [disabled]="!selectedCfgFormVersion"
            (click)="setCenterMode('edit')"
            data-cy="paragraph-builder-mode-edit">
            <lucide-icon name="pencil" [size]="16"></lucide-icon>
            <span>Edit</span>
          </button>
          <button type="button"
            class="mode-switch-option"
            [class.is-active]="centerMode === 'preview'"
            [attr.aria-pressed]="centerMode === 'preview'"
            [disabled]="!selectedCfgFormVersion"
            (click)="setCenterMode('preview')"
            data-cy="paragraph-builder-mode-preview">
            <lucide-icon name="eye" [size]="16"></lucide-icon>
            <span>Preview</span>
          </button>
        </div>
        <button mat-raised-button
          (click)="openPreview()"
          matTooltip="Open the form preview in a full-screen window"
          [disabled]="!selectedCfgFormVersion"
          data-cy="paragraph-builder-full-screen-preview">
          <lucide-icon name="external-link" [size]="20"></lucide-icon>
          Full screen
        </button>
        <button mat-raised-button
          (click)="openAiStudio('pdf')"
          [disabled]="!formIsEditable || !selectedCfgFormVersion"
          matTooltip="Import a PDF or Word form and replace the current form">
          <mat-icon>upload_file</mat-icon>
          Import Document
        </button>
        <button mat-raised-button
          color="primary"
          (click)="saveFormData()"
          [disabled]="!formIsEditable">
          <lucide-icon name="save" [size]="20"></lucide-icon>
          Save
        </button>
      </div>
    </div>
    <!-- Unsaved AI changes. The Studio commits only to the in-memory version, so leaving the
         editor without saving silently discards everything it generated. Stays up until a save
         succeeds; deliberately not dismissible for that reason. -->
    @if (aiChangesAwaitingSave) {
      <div class="ai-unsaved-banner" role="alert">
        <lucide-icon name="alert-triangle" [size]="28" class="ai-unsaved-icon"></lucide-icon>
        <div class="ai-unsaved-text">
          @if (aiChangesAwaitingSaveMode === 'importedVersion') {
            <span class="ai-unsaved-title">Imported form needs to be saved</span>
            <span class="ai-unsaved-detail">
              The import was created as a draft version, but its fields aren't fully set up until
              you click Save. Review the form, then save it before using or publishing it.
            </span>
          } @else {
            <span class="ai-unsaved-title">
              {{ aiChangesAwaitingSaveMode === 'import' ? 'Imported form is not saved yet' : 'AI changes are not saved yet' }}
            </span>
            <span class="ai-unsaved-detail">
              These changes exist only in this editor. You must click Save for them to remain —
              leaving or reloading this page will discard them.
            </span>
          }
        </div>
        <button mat-raised-button
          color="primary"
          class="ai-unsaved-save"
          (click)="saveFormData()"
          [disabled]="!formIsEditable || isSaving">
          <lucide-icon name="save" [size]="20"></lucide-icon>
          Save now
        </button>
      </div>
    }
    <!-- Main Content Area -->
    <div class="main-content">
      <!-- Left Sidebar: Paragraphs -->
      <div class="paragraphs-sidebar">
        <div class="sidebar-header">
          <h3>Paragraphs</h3>
          <div class="sidebar-header-actions">
            <button mat-icon-button
              matTooltip="Help & Tutorial"
              (click)="openTutorial()">
              <lucide-icon name="circle-help" [size]="20"></lucide-icon>
            </button>
            <button mat-icon-button
              [matTooltip]="getKeyboardShortcutsTooltip()"
              matTooltipClass="keyboard-shortcuts-tooltip">
              <lucide-icon name="keyboard" [size]="20"></lucide-icon>
            </button>
            <button mat-icon-button
              matTooltip="Create new paragraph"
              [disabled]="!formIsEditable"
              (click)="createNewDFormParagraph()">
              <lucide-icon name="plus" [size]="20"></lucide-icon>
            </button>
          </div>
        </div>
        <mat-tab-group [(selectedIndex)]="paragraphTabIndex" (selectedIndexChange)="onParagraphTabChange($event)">
          <!-- List Tab -->
          <mat-tab>
            <ng-template mat-tab-label>
              <lucide-icon name="list" [size]="20"></lucide-icon>
              <span class="tab-label">List ({{ visibleParagraphs().length }})</span>
            </ng-template>
            <div class="paragraph-list"
              cdkDropList
              (cdkDropListDropped)="paragraphReOrderEvent($event)">
              @for (paragraph of visibleParagraphs(); track paragraph; let i = $index) {
                <div class="paragraph-item"
                  [class.selected]="paragraph.DFormParagraphId === selectedParagraphId"
                  [cdkDragDisabled]="!formIsEditable"
                  cdkDrag
                  cdkDragLockAxis="y"
                  (click)="selectDFormParagraph(paragraph)">
                  <span class="paragraph-number">{{ i + 1 }}.</span>
                  <span class="paragraph-name">{{ paragraph.ParagraphName }}</span>
                  @if (formIsEditable) {
                    <button mat-icon-button class="paragraph-action-btn" matTooltip="Duplicate paragraph"
                      (click)="duplicateParagraph(paragraph); $event.stopPropagation()"
                      >
                      <lucide-icon name="copy" [size]="20"></lucide-icon>
                    </button>
                  }
                  @if (formIsEditable) {
                    <lucide-icon name="grip-vertical" [size]="20" class="drag-handle" cdkDragHandle></lucide-icon>
                  }
                </div>
              }
              @if (!selectedCfgFormVersion?.cfgDFormParagraphs?.length) {
                <div class="empty-paragraphs">
                  <lucide-icon name="align-justify" [size]="20"></lucide-icon>
                  <p>No paragraphs yet</p>
                  <button mat-raised-button color="primary" (click)="createNewDFormParagraph()" [disabled]="!formIsEditable" class="add-first-paragraph-btn">
                    <lucide-icon name="plus" [size]="16"></lucide-icon>
                    Add First Paragraph
                  </button>
                </div>
              }
            </div>
          </mat-tab>
          <!-- Properties Tab -->
          <mat-tab>
            <ng-template mat-tab-label>
              <lucide-icon name="settings" [size]="20"></lucide-icon>
              <span class="tab-label">Properties</span>
            </ng-template>
            @if (selectedCfgFormParagraph) {
              <div class="paragraph-properties">
                <div class="property-field">
                  <label>Paragraph Name</label>
                  <input type="text"
                    [(ngModel)]="selectedCfgFormParagraph.ParagraphName"
                    [disabled]="!formIsEditable"
                    (input)="onParagraphNameChange()"
                    maxlength="50"
                    class="property-input">
                  </div>
                  <div class="property-field">
                    <label>Orientation</label>
                    <mat-select [(ngModel)]="activeParagraphOrientation"
                      [disabled]="!formIsEditable"
                      (selectionChange)="selectParagraphOrientation($event.value)">
                      @for (o of cfgFormParagraphOrientationList; track o) {
                        <mat-option [value]="o.id">
                          {{ o.name }}
                        </mat-option>
                      }
                    </mat-select>
                  </div>
                  <div class="property-field">
                    <label>Item Layout</label>
                    <mat-select [(ngModel)]="activeParagraphItemLayout"
                      [disabled]="!formIsEditable"
                      (selectionChange)="selectParagraphItemLayout($event.value)">
                      @for (l of cfgFormItemLayoutList; track l) {
                        <mat-option [value]="l.id">
                          {{ l.name }}
                        </mat-option>
                      }
                    </mat-select>
                  </div>
                  <div class="property-checkboxes">
                    <mat-checkbox [(ngModel)]="selectedCfgFormParagraph.PageBreakAfter"
                      [disabled]="!formIsEditable"
                      (change)="syncLivePreview()">
                      Page break after paragraph
                    </mat-checkbox>
                    <mat-checkbox [(ngModel)]="selectedCfgFormParagraph.isRepeting"
                      [disabled]="!formIsEditable"
                      (change)="syncLivePreview()">
                      Is repeatable
                    </mat-checkbox>
                  </div>
                  <div class="property-actions">
                    <button mat-raised-button color="primary" (click)="duplicateParagraph()" [disabled]="!formIsEditable" class="property-action-btn">
                      <lucide-icon name="copy" [size]="16"></lucide-icon>
                      Duplicate Paragraph
                    </button>
                    <button mat-raised-button color="warn" (click)="deleteParagraph()" [disabled]="!formIsEditable" class="property-action-btn">
                      <lucide-icon name="trash-2" [size]="16"></lucide-icon>
                      Delete Paragraph
                    </button>
                  </div>
                </div>
              }
              @if (!selectedCfgFormParagraph) {
                <div class="no-selection">
                  <lucide-icon name="pointer" [size]="20"></lucide-icon>
                  <p>Select a paragraph to view properties</p>
                </div>
              }
            </mat-tab>
          </mat-tab-group>
        </div>
        <!-- Center: Builder Area -->
        <div class="builder-area">
          <!-- Paragraph Builder -->
          <div class="paragraph-builder" [class.preview-mode]="centerMode === 'preview'">
            <!-- Edit mode only: in preview the top bar already names the mode, and a second header
                 row here just pushed the rendered form further down the pane. -->
            @if (centerMode === 'edit') {
              <div class="builder-header">
                <h4>{{ livePreviewMode ? 'Live Preview Builder' : 'Paragraph Builder' }}</h4>
                @if (selectedParagraphOrganizer) {
                  <span class="item-count">{{ getTotalItemCount() }} items</span>
                }
              </div>
            }
            <!-- Empty States -->
            @if (!selectedCfgFormVersion) {
              <div class="empty-state">
                <lucide-icon name="alert-circle" [size]="20"></lucide-icon>
                <h3>No Version Selected</h3>
                <p>Please go back and select a version to edit.</p>
                <button mat-raised-button color="primary" (click)="goBack()">
                  <lucide-icon name="arrow-left" [size]="20"></lucide-icon>
                  Go Back
                </button>
              </div>
            }
            <!-- Whole-form preview: what the editor opens on, so the user sees their form
                 immediately instead of an empty pane. -->
            @if (centerMode === 'preview' && selectedCfgFormVersion) {
              @if (visibleParagraphs().length) {
                <div class="center-preview" data-cy="paragraph-builder-center-preview">
                  <div class="center-preview-host">
                    <app-forms-builder-polished-preview
                      [formVersion]="previewFormVersion"
                      [embedded]="true"
                      [hideChrome]="true"
                      [useDocumentFrame]="previewUseDocumentFrame"
                      [mobilePreview]="previewMobile">
                    </app-forms-builder-polished-preview>
                  </div>
                </div>
              } @else {
                <div class="empty-state" data-cy="paragraph-builder-empty-version">
                  <lucide-icon name="file-text" [size]="20"></lucide-icon>
                  <h3>This version has no content yet</h3>
                  <p>Add a paragraph to start building, or import a PDF or Word document and let the AI draft it for you.</p>
                  <button mat-raised-button (click)="openAiStudio('pdf')" [disabled]="!formIsEditable">
                    <mat-icon>upload_file</mat-icon>
                    Import Document
                  </button>
                  <button mat-raised-button color="accent" (click)="openAiStudio('chat')" [disabled]="!formIsEditable">
                    <mat-icon>auto_awesome</mat-icon>
                    AI Edit Assistant
                  </button>
                  <button mat-raised-button color="primary" (click)="createNewDFormParagraph()" [disabled]="!formIsEditable">
                    <lucide-icon name="plus" [size]="20"></lucide-icon>
                    Create Paragraph
                  </button>
                </div>
              }
            }
            @if (centerMode === 'edit' && selectedCfgFormVersion && !selectedCfgFormParagraph) {
              <div class="empty-state">
                <lucide-icon name="align-justify" [size]="20"></lucide-icon>
                <h3>No Paragraph Selected</h3>
                <p>Select a paragraph from the list or create a new one to start building.</p>
                <button mat-raised-button (click)="openAiStudio('pdf')" [disabled]="!formIsEditable">
                  <mat-icon>upload_file</mat-icon>
                  Import Document
                </button>
                <button mat-raised-button color="accent" (click)="openAiStudio('chat')" [disabled]="!formIsEditable">
                  <mat-icon>auto_awesome</mat-icon>
                  AI Edit Assistant
                </button>
                <button mat-raised-button color="primary" (click)="createNewDFormParagraph()" [disabled]="!formIsEditable">
                  <lucide-icon name="plus" [size]="20"></lucide-icon>
                  Create Paragraph
                </button>
              </div>
            }
            <!-- Row Builder -->
            @if (centerMode === 'edit' && selectedParagraphOrganizer) {
              <div class="row-builder"
                [class.live-preview-mode]="livePreviewMode">
                <!-- Insert Row at Top — sits inside the scroll container but outside the cdkDropList
                     so CDK placeholder shifts don't cause the first row to jump -->
                @if (formIsEditable) {
                  <div class="insert-row-zone">
                    <button mat-stroked-button color="primary" (click)="insertRowAt(0)">
                      <lucide-icon name="plus" [size]="20"></lucide-icon>
                      Insert Row at Top
                    </button>
                  </div>
                }
                <div class="row-reorder-list"
                  cdkDropList
                  (cdkDropListDropped)="dropEventOnRow($event)">
                <!-- Rows -->
                @for (row of selectedParagraphOrganizer; track trackByRowIndex(i, row); let i = $index) {
                  <div class="row-wrapper"
                    cdkDrag
                    [cdkDragDisabled]="!formIsEditable"
                    cdkDragLockAxis="y">
                    @if (formIsEditable) {
                      <div class="row-indicator">
                        <span class="row-number">{{ i + 1 }}</span>
                        @if (row.length === 0) {
                          <button mat-icon-button
                            class="delete-row-btn"
                            (click)="deleteRowAt(i)"
                            matTooltip="Delete empty row">
                            <lucide-icon name="trash-2" [size]="20"></lucide-icon>
                          </button>
                        }
                      </div>
                    }
                    @if (formIsEditable) {
                      <div class="row-drag-handle" cdkDragHandle>
                        <lucide-icon name="grip-vertical" [size]="20"></lucide-icon>
                        <span>Row {{ i + 1 }}</span>
                      </div>
                    }
                    <div class="builder-row"
                      [class.builder-row-live]="livePreviewMode"
                      cdkDropList
                      [cdkDropListConnectedTo]="connectedDragDropParagraphs"
                      [cdkDropListData]="row"
                      cdkDropListOrientation="horizontal"
                      (cdkDropListDropped)="dropEventInRow($event, row, i)"
                      [id]="'paragraphRow' + i"
                      [class.empty-row]="row.length === 0">
                      @if (row.length === 0) {
                        <div class="empty-row-message">
                          <lucide-icon name="inbox" [size]="20"></lucide-icon>
                          <span>Drag items here</span>
                        </div>
                      }
                      <!-- ========== LIVE PREVIEW MODE ========== -->
                      @if (livePreviewMode) {
                        @for (item of row; track trackByItemId(j, item); let j = $index) {
                          <div class="builder-item-wrapper"
                            cdkDrag
                            [cdkDragData]="item"
                            [cdkDragDisabled]="!formIsEditable"
                            [style.flex-basis]="item.ParagraphColumnWidth > 0 && item.ParagraphColumnWidth <= 100 ? item.ParagraphColumnWidth + '%' : '0%'"
                            [style.flex-grow]="(!item.ParagraphColumnWidth || item.ParagraphColumnWidth <= 0 || item.ParagraphColumnWidth > 100) ? 1 : 0"
                            [style.flex-shrink]="1"
                            [style.min-width.px]="0"
                            [class.selected]="selectedItemId === item.DFormItemId"
                            (mouseenter)="hoveredItem = item"
                            (mouseleave)="hoveredItem = null"
                            (click)="onItemClick(item, i, j, $event)">
                            <!-- Custom drag preview (compact card instead of full rendered item) -->
                            <div *cdkDragPreview class="drag-preview">
                              <mat-icon>{{ getItemLabel(item).icon }}</mat-icon>
                              <span>{{ getItemLabel(item).name }}: {{ item.Label || item.DFormItemName || 'Unnamed' }}</span>
                            </div>
                            <!-- Drag placeholder -->
                            <div *cdkDragPlaceholder class="drag-placeholder"></div>
                            @if (!item.xauditDeleted) {
                              <div class="builder-item-inner">
                                <!-- Actual form item rendering (locked/read-only) -->
                                <app-forms-item
                                  [dataFormItem]="getSpoofDataForItem(item)"
                                  [isLocked]="true"
                                  [actionsEnabled]="false"
                                  [saveEmitter]="builderNoopEmitter"
                                  [sideBySide]="false"
                                  [paragraphOrder]="0"
                                  [itemIndex]="j">
                                </app-forms-item>
                              </div>
                            }
                            <!-- Builder overlay (visible on hover/select) - moved outside inner to avoid overflow clipping -->
                            @if (!item.xauditDeleted) {
                              <div class="builder-overlay"
                                [class.visible]="hoveredItem === item || selectedItemId === item.DFormItemId"
                                (mouseenter)="hoveredItem = item"
                                (mouseleave)="hoveredItem = null">
                                <div class="builder-actions" (click)="$event.stopPropagation()">
                                  @if (formIsEditable) {
                                    <button mat-icon-button
                                      [matTooltip]="item.isRequired ? 'Remove required' : 'Mark required'"
                                      (click)="toggleRequired(item)">
                                      <lucide-icon name="siren" [size]="20" [class.required-active]="item.isRequired"></lucide-icon>
                                    </button>
                                  }
                                  @if (formIsEditable) {
                                    <button mat-icon-button
                                      matTooltip="Duplicate"
                                      (click)="quickDuplicate(item, i)">
                                      <lucide-icon name="copy" [size]="20"></lucide-icon>
                                    </button>
                                  }
                                  @if (formIsEditable && isInlineEditable(item)) {
                                    <button mat-icon-button
                                      matTooltip="Expand to edit full text"
                                      (click)="toggleItemExpanded(item, $event)">
                                      <lucide-icon name="maximize-2" [size]="20"></lucide-icon>
                                    </button>
                                  }
                                  <button mat-icon-button
                                    matTooltip="Settings"
                                    (click)="openItemDetailDetail(item, i, j)">
                                    <lucide-icon name="settings" [size]="20"></lucide-icon>
                                  </button>
                                  @if (formIsEditable) {
                                    <button mat-icon-button
                                      matTooltip="Delete"
                                      (click)="deleteItemFromRow(item, i, j)">
                                      <lucide-icon name="trash-2" [size]="20"></lucide-icon>
                                    </button>
                                  }
                                </div>
                              </div>
                            }
                          </div>
                        }
                      }
                      <!-- ========== CLASSIC CARD MODE ========== -->
                      @if (!livePreviewMode) {
                        @for (item of row; track trackByItemId(j, item); let j = $index) {
                          <div class="item-card"
                            [class.inline-editable]="isInlineEditable(item)"
                            cdkDrag
                            [cdkDragData]="item"
                            [cdkDragDisabled]="!formIsEditable">
                            @if (!item.xauditDeleted) {
                              <div class="item-card-content">
                                <mat-icon class="item-type-icon">{{ getItemLabel(item).icon }}</mat-icon>
                                <span class="item-type-badge">{{ getItemLabel(item).name }}</span>
                                <!-- Non-inline editable items: click opens dialog -->
                                @if (!isInlineEditable(item)) {
                                  <div class="item-info"
                                    (click)="openItemDetailDetail(item, i, j)">
                                    <span class="item-name">{{ item.DFormItemName || 'Unnamed' }}</span>
                                    @if (item.Label) {
                                      <span class="item-label">{{ item.Label }}</span>
                                    }
                                  </div>
                                }
                                <!-- Inline editable text field -->
                                @if (isInlineEditable(item)) {
                                  <div class="item-inline-edit">
                                    @if (!isMultilineControl(item)) {
                                      <input
                                        type="text"
                                        [(ngModel)]="item.Label"
                                        [placeholder]="getPlaceholderForType(item)"
                                        [disabled]="!formIsEditable"
                                        [style.width.ch]="(item.Label?.length || 10) + 2"
                                        (blur)="onInlineEditBlur(item)"
                                        (keydown.enter)="onInlineEditEnter($event)"
                                        (click)="$event.stopPropagation()"
                                        class="inline-edit-input">
                                    }
                                    @if (isMultilineControl(item)) {
                                      <textarea
                                        [(ngModel)]="item.Label"
                                        [placeholder]="getPlaceholderForType(item)"
                                        [disabled]="!formIsEditable"
                                        (blur)="onInlineEditBlur(item)"
                                        (click)="$event.stopPropagation()"
                                        rows="2"
                                      class="inline-edit-textarea"></textarea>
                                    }
                                    @if (formIsEditable && (hasLongText(item) || isMultilineControl(item))) {
                                      <button mat-icon-button
                                        (click)="toggleItemExpanded(item, $event)"
                                        matTooltip="Expand to edit full text"
                                        class="expand-text-btn">
                                        <lucide-icon name="maximize-2" [size]="20"></lucide-icon>
                                      </button>
                                    }
                                  </div>
                                }
                                <!-- Quick actions toolbar -->
                                <div class="item-quick-actions" (click)="$event.stopPropagation()">
                                  @if (formIsEditable) {
                                    <button mat-icon-button
                                      [matTooltip]="item.isRequired ? 'Remove required' : 'Mark required'"
                                      (click)="toggleRequired(item)">
                                      <lucide-icon name="siren" [size]="20" [class.required-active]="item.isRequired"></lucide-icon>
                                    </button>
                                  }
                                  @if (formIsEditable) {
                                    <button mat-icon-button
                                      matTooltip="Duplicate"
                                      (click)="quickDuplicate(item, i)">
                                      <lucide-icon name="copy" [size]="20"></lucide-icon>
                                    </button>
                                  }
                                  <button mat-icon-button
                                    matTooltip="More settings"
                                    (click)="openItemDetailDetail(item, i, j)">
                                    <lucide-icon name="settings" [size]="20"></lucide-icon>
                                  </button>
                                  @if (formIsEditable) {
                                    <button mat-icon-button
                                      matTooltip="Delete"
                                      (click)="deleteItemFromRow(item, i, j)">
                                      <lucide-icon name="trash-2" [size]="20"></lucide-icon>
                                    </button>
                                  }
                                </div>
                              </div>
                            }
                          </div>
                        }
                      }
                    </div>
                    <!-- Insert Row Between -->
                    @if (formIsEditable) {
                      <div class="insert-row-zone insert-between">
                        <button mat-stroked-button color="primary" class="compact" (click)="insertRowAt(i + 1)">
                          <lucide-icon name="plus" [size]="20"></lucide-icon>
                          Insert Row
                        </button>
                      </div>
                    }
                  </div>
                }
                <!-- Add First Row -->
                @if (formIsEditable && selectedParagraphOrganizer.length === 0) {
                  <div class="add-first-row">
                    <button mat-raised-button color="primary" (click)="addParagraphRowToOrganizer()">
                      <lucide-icon name="plus" [size]="20"></lucide-icon>
                      Add First Row
                    </button>
                  </div>
                }
                <!-- Auto-create row drop zone -->
                @if (shouldShowAutoRowDropZone()) {
                  <div class="auto-row-drop-zone"
                    cdkDropList
                    [cdkDropListConnectedTo]="connectedDragDropParagraphs"
                    [id]="'autoCreateRow'"
                    (cdkDropListDropped)="dropEventAutoCreateRow($event)">
                    <div class="drop-zone-content">
                      <lucide-icon name="plus-circle" [size]="20"></lucide-icon>
                      <span>Drop here to add new row</span>
                    </div>
                  </div>
                }
                </div><!-- /row-reorder-list -->
              </div>
            }
          </div>
        </div>
        <!-- Right Sidebar: Items -->
        <div class="items-sidebar">
          <div class="sidebar-header">
            <h3>Items</h3>
            @if (formIsEditable) {
              <mat-icon class="help-icon"
                matTooltip="Drag items into the builder to add them to your form"
                >
                help_outline
              </mat-icon>
            }
          </div>
          <!-- Item Search -->
          <mat-form-field class="item-search" appearance="outline">
            <lucide-icon name="search" [size]="20" matPrefix></lucide-icon>
            <input matInput
              [(ngModel)]="itemSearchQuery"
              (input)="filterItems()"
              data-cy="paragraph-builder-item-search-input">
              @if (itemSearchQuery) {
                <button mat-icon-button matSuffix (click)="clearItemSearch()">
                  <lucide-icon name="x" [size]="20"></lucide-icon>
                </button>
              }
            </mat-form-field>
            <!-- Item Groups -->
            <div class="item-groups"
              id="itemListDropList"
              cdkDropList
              [cdkDropListConnectedTo]="connectedDragDropParagraphs"
              [cdkDropListData]="cfgFormItemTypeList"
              (cdkDropListDropped)="organizeItemTypeList()">
              <mat-accordion>
                <!-- Quick Add Templates -->
                <mat-expansion-panel [(expanded)]="quickAddExpanded">
                  <mat-expansion-panel-header>
                    <mat-panel-title>
                      <lucide-icon name="zap" [size]="20"></lucide-icon>
                      <span>Quick Add</span>
                    </mat-panel-title>
                  </mat-expansion-panel-header>
                  <button mat-raised-button
                    class="ai-assistant-btn"
                    [disabled]="!formIsEditable || !selectedCfgFormVersion"
                    (click)="openAiStudio('pdf')">
                    <mat-icon>upload_file</mat-icon>
                    Import Document
                  </button>
                  <button mat-stroked-button
                    class="ai-assistant-btn"
                    color="accent"
                    [disabled]="!formIsEditable || !selectedCfgFormVersion"
                    (click)="openAiStudio('chat')">
                    <mat-icon>auto_awesome</mat-icon>
                    AI Edit
                  </button>
                  <div class="prebuilt-categories"
                    id="prebuiltDropList"
                    cdkDropList
                    [cdkDropListConnectedTo]="connectedDragDropParagraphs"
                    [cdkDropListData]="[]"
                    [cdkDropListSortingDisabled]="true">
                    @for (category of prebuiltTemplates; track category) {
                      <div class="prebuilt-category">
                        <div class="category-header">
                          <mat-icon>{{ category.icon }}</mat-icon>
                          <span>{{ category.category }}</span>
                        </div>
                        <div class="category-fields">
                          @for (field of category.fields; track field) {
                            <div class="prebuilt-field"
                              cdkDrag
                              [cdkDragData]="field"
                              [cdkDragDisabled]="!formIsEditable"
                              (click)="addPrebuiltToCurrentRow(field)">
                              <lucide-icon name="grip-vertical" [size]="20" class="drag-icon"></lucide-icon>
                              <mat-icon class="field-icon">{{ field.icon }}</mat-icon>
                              <span class="field-name">{{ field.name }}</span>
                            </div>
                          }
                        </div>
                      </div>
                    }
                    <!-- Custom Templates -->
                    @for (group of customTemplateGroups; track group) {
                      <div class="prebuilt-category custom-template">
                        <div class="category-header">
                          <mat-icon>{{ group.icon }}</mat-icon>
                          <span>{{ group.category }}</span>
                          <button mat-icon-button class="delete-template-btn"
                            matTooltip="Delete template"
                            (click)="deleteCustomTemplate(group); $event.stopPropagation()">
                            <lucide-icon name="x" [size]="20"></lucide-icon>
                          </button>
                        </div>
                        <div class="category-fields">
                          @for (field of group.fields; track field) {
                            <div class="prebuilt-field"
                              cdkDrag
                              [cdkDragData]="field"
                              [cdkDragDisabled]="!formIsEditable"
                              (click)="addPrebuiltToCurrentRow(field)">
                              <lucide-icon name="grip-vertical" [size]="20" class="drag-icon"></lucide-icon>
                              <mat-icon class="field-icon">{{ field.icon }}</mat-icon>
                              <span class="field-name">{{ field.name }}</span>
                            </div>
                          }
                        </div>
                      </div>
                    }
                    <!-- Save as Template Button -->
                    @if (selectedParagraphOrganizer?.length > 0 && formIsEditable) {
                      <button mat-stroked-button class="save-template-btn"
                        (click)="saveSelectionAsTemplate()"
                        matTooltip="Save current paragraph items as a reusable template">
                        <lucide-icon name="bookmark-plus" [size]="20"></lucide-icon>
                        Save as Template
                      </button>
                    }
                  </div>
                </mat-expansion-panel>
                <!-- Recently Used Section -->
                @if (recentlyUsedItems.length > 0) {
                  <mat-expansion-panel
                    class="item-category-panel recently-used-panel"
                    [expanded]="true">
                    <mat-expansion-panel-header>
                      <mat-panel-title>
                        <lucide-icon name="history" [size]="20" class="category-icon"></lucide-icon>
                        <span>Recently Used</span>
                        <span class="item-count">{{ recentlyUsedItems.length }}</span>
                      </mat-panel-title>
                    </mat-expansion-panel-header>
                    @for (item of recentlyUsedItems; track item) {
                      <div class="item-type"
                        cdkDrag
                        [cdkDragData]="item"
                        [cdkDragDisabled]="!formIsEditable"
                        [matTooltip]="item.description || item.name"
                        matTooltipPosition="left"
                        [matTooltipShowDelay]="650"
                        [matTooltipHideDelay]="100"
                        matTooltipClass="item-list-tooltip">
                        <lucide-icon name="grip-vertical" [size]="20" class="drag-icon"></lucide-icon>
                        <mat-icon class="item-icon">{{ item.icon }}</mat-icon>
                        <span class="item-type-name">{{ item.name }}</span>
                      </div>
                    }
                  </mat-expansion-panel>
                }
                <!-- Categorized Items -->
                @for (entry of filteredCategorizedItems | keyvalue; track entry) {
                  <mat-expansion-panel
                    class="item-category-panel"
                    [expanded]="false">
                    <mat-expansion-panel-header>
                      <mat-panel-title>
                        <span>{{ entry.key }}</span>
                        <span class="item-count">{{ entry.value.length }}</span>
                      </mat-panel-title>
                    </mat-expansion-panel-header>
                    @for (item of entry.value; track item) {
                      <div class="item-type"
                        cdkDrag
                        [cdkDragData]="item"
                        [cdkDragDisabled]="!formIsEditable"
                        [matTooltip]="item.description || item.name"
                        matTooltipPosition="left"
                        [matTooltipShowDelay]="650"
                        [matTooltipHideDelay]="100"
                        matTooltipClass="item-list-tooltip">
                        <lucide-icon name="grip-vertical" [size]="20" class="drag-icon"></lucide-icon>
                        <mat-icon class="item-icon">{{ item.icon }}</mat-icon>
                        <span class="item-type-name">{{ item.name }}</span>
                      </div>
                    }
                    @if (entry.value.length === 0) {
                      <div class="no-items">
                        No items found
                      </div>
                    }
                  </mat-expansion-panel>
                }
              </mat-accordion>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Expanded Text Edit Modal -->
    @if (expandedItemId && getExpandedItem()) {
      <div class="expanded-edit-overlay" (click)="collapseItem()">
        <div class="expanded-edit-container" (click)="$event.stopPropagation()">
          <div class="expanded-edit-header">
            <div class="expanded-edit-title">
              <mat-icon>{{ getItemLabel(getExpandedItem()).icon }}</mat-icon>
              <span>{{ getItemLabel(getExpandedItem()).name }}: {{ getExpandedItem().DFormItemName || 'Unnamed' }}</span>
            </div>
            <button mat-icon-button (click)="collapseItem()" matTooltip="Close (Esc)">
              <lucide-icon name="x" [size]="20"></lucide-icon>
            </button>
          </div>
          <textarea [(ngModel)]="getExpandedItem().Label"
            [placeholder]="getPlaceholderForType(getExpandedItem())"
            [disabled]="!formIsEditable"
            class="expanded-textarea"
          #expandedTextarea></textarea>
        </div>
      </div>
    }

    <!-- Loading Overlay -->
    @if (isLoading) {
      <div class="loading-overlay">
        <mat-spinner></mat-spinner>
      </div>
    }
